import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingDown, TrendingUp } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { analyticsApi, type AnalyticsPeriod } from "@/features/analytics/api";
import { cn } from "@/lib/utils";
import { useState } from "react";

const PERIODS: { value: AnalyticsPeriod; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

function shortLabel(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function ChangeIndicator({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  const positive = value >= 0;
  const pct = Math.round(value * 100);
  const label = pct >= 0 ? `+${pct}%` : `${pct}%`;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div
      className={cn(
        "mt-1 inline-flex items-center gap-1 text-xs",
        positive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400",
      )}
    >
      <Icon className="size-3" />
      <span>
        {label} <span className="text-muted-foreground">vs prev</span>
      </span>
    </div>
  );
}

function StatCard({
  label,
  value,
  change,
}: {
  label: string;
  value: string | number;
  change?: number | null;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        <ChangeIndicator value={change} />
      </CardContent>
    </Card>
  );
}

export default function AgentAnalyticsTab({ agentId }: { agentId: string }) {
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");

  const overviewQ = useQuery({
    queryKey: ["analytics", "overview", agentId, period],
    queryFn: () => analyticsApi.overview({ period, agent_id: agentId }),
    staleTime: 30_000,
    retry: false,
  });

  const callsQ = useQuery({
    queryKey: ["analytics", "calls", agentId, period],
    queryFn: () =>
      analyticsApi.calls({ period, group_by: "day", agent_id: agentId }),
    staleTime: 30_000,
    retry: false,
  });

  const chartData = useMemo(
    () =>
      (callsQ.data?.data ?? []).map((d) => ({
        ...d,
        label: shortLabel(d.period),
      })),
    [callsQ.data],
  );

  const overview = overviewQ.data;
  const calls = overview?.calls;
  const sentiment = overview?.sentiment;
  const costs = overview?.costs;
  const loading = overviewQ.isLoading || callsQ.isLoading;
  const hasData = !!calls && calls.total > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Agent Analytics</h2>
        <Select
          value={period}
          onValueChange={(v) => setPeriod(v as AnalyticsPeriod)}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      )}

      {!loading && !hasData && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No call data for this agent in the selected period.
          </CardContent>
        </Card>
      )}

      {!loading && hasData && (
        <>
          <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-6">
            <StatCard
              label="Total Calls"
              value={calls!.total}
              change={calls!.change_from_previous?.total}
            />
            <StatCard
              label="Completed"
              value={calls!.completed}
              change={calls!.change_from_previous?.completed}
            />
            <StatCard label="Failed" value={calls!.failed} />
            <StatCard
              label="Success Rate"
              value={`${calls!.success_rate ?? 0}%`}
            />
            <StatCard
              label="Total Min"
              value={(calls!.total_minutes ?? 0).toFixed(1)}
            />
            <StatCard
              label="Avg Duration"
              value={`${Math.round(calls!.avg_duration_seconds ?? 0)}s`}
            />
          </div>

          {chartData.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-1">
                  <CardTitle className="text-sm">Calls Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11 }}
                          stroke="currentColor"
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 11 }}
                          stroke="currentColor"
                        />
                        <Tooltip
                          contentStyle={{
                            background: "var(--background)",
                            border: "1px solid var(--border)",
                            borderRadius: 6,
                            fontSize: 12,
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="total_calls"
                          name="Calls"
                          stroke="#6366f1"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-1">
                  <CardTitle className="text-sm">Outcome Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11 }}
                          stroke="currentColor"
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 11 }}
                          stroke="currentColor"
                        />
                        <Tooltip
                          contentStyle={{
                            background: "var(--background)",
                            border: "1px solid var(--border)",
                            borderRadius: 6,
                            fontSize: 12,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar
                          dataKey="completed"
                          name="Completed"
                          stackId="a"
                          fill="#22c55e"
                        />
                        <Bar
                          dataKey="failed"
                          name="Failed"
                          stackId="a"
                          fill="#ef4444"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm">Sentiment</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <SentimentChip
                  label="Positive"
                  value={sentiment?.positive ?? 0}
                  cls="border-emerald-200 text-emerald-700 dark:border-emerald-900 dark:text-emerald-400"
                />
                <SentimentChip
                  label="Neutral"
                  value={sentiment?.neutral ?? 0}
                  cls="border-input text-muted-foreground"
                />
                <SentimentChip
                  label="Negative"
                  value={sentiment?.negative ?? 0}
                  cls="border-rose-200 text-rose-700 dark:border-rose-900 dark:text-rose-400"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm">Estimated Cost</CardTitle>
                <CardDescription>
                  Across all calls in the selected period.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold tabular-nums">
                  ${((costs?.total_cents ?? 0) / 100).toFixed(2)}
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function SentimentChip({
  label,
  value,
  cls,
}: {
  label: string;
  value: number;
  cls: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs",
        cls,
      )}
    >
      <span className="font-medium">{label}:</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}
